/**
 * Axios API Configuration
 * Base Axios instance with interceptors for request/response handling
 */

import axios from 'axios';
import axiosRetry from 'axios-retry';
import { API_CONFIG, AUTH_CONFIG } from '../constants/config';

// Create axios instance
const api = axios.create({
  baseURL: API_CONFIG.baseURL,
  timeout: API_CONFIG.timeout,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Configure automatic retry for failed requests
axiosRetry(api, {
  retries: 3, // Number of retry attempts
  retryDelay: axiosRetry.exponentialDelay, // Exponential backoff
  retryCondition: (error) => {
    // Retry on network errors or 5xx server errors
    return axiosRetry.isNetworkOrIdempotentRequestError(error) || 
           (error.response?.status >= 500 && error.response?.status <= 599);
  },
  onRetry: (retryCount, error, requestConfig) => {
    console.log(`🔄 Retry attempt ${retryCount} for ${requestConfig.url}`);
  },
});

// Request cancellation controller storage
const cancelTokens = new Map();

/**
 * Cancel a pending request by key
 * @param {string} key - Unique identifier for the request
 */
export const cancelRequest = (key) => {
  const controller = cancelTokens.get(key);
  if (controller) {
    controller.abort();
    cancelTokens.delete(key);
    console.log(`🚫 Request cancelled: ${key}`);
  }
};

/**
 * Cancel all pending requests
 */
export const cancelAllRequests = () => {
  cancelTokens.forEach((controller, key) => {
    controller.abort();
    console.log(`🚫 Request cancelled: ${key}`);
  });
  cancelTokens.clear();
};

// Request interceptor - Add auth token to requests
api.interceptors.request.use(
  (config) => {
    // Get token from localStorage
    const token = localStorage.getItem(AUTH_CONFIG.tokenKey);
    
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Add request cancellation support
    if (config.cancelKey) {
      const controller = new AbortController();
      config.signal = controller.signal;
      cancelTokens.set(config.cancelKey, controller);
    }
    
    // Log request in development
    if (process.env.NODE_ENV === 'development') {
      console.log('🚀 API Request:', {
        method: config.method?.toUpperCase(),
        url: config.url,
        data: config.data,
        cancelKey: config.cancelKey,
      });
    }
    
    return config;
  },
  (error) => {
    console.error('❌ Request Error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor - Handle responses and errors
api.interceptors.response.use(
  (response) => {
    // Remove cancel token if request completed successfully
    if (response.config.cancelKey) {
      cancelTokens.delete(response.config.cancelKey);
    }

    // Log response in development
    if (process.env.NODE_ENV === 'development') {
      console.log('✅ API Response:', {
        status: response.status,
        url: response.config.url,
        data: response.data,
      });
    }
    
    return response.data;
  },
  async (error) => {
    // Remove cancel token
    if (error.config?.cancelKey) {
      cancelTokens.delete(error.config.cancelKey);
    }

    // Handle request cancellation
    if (axios.isCancel(error) || error.code === 'ERR_CANCELED') {
      console.log('🚫 Request was cancelled:', error.config?.url);
      return Promise.reject({
        message: 'Request cancelled',
        cancelled: true,
      });
    }

    const originalRequest = error.config;
    
    // Handle 401 Unauthorized - Token expired
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      
      try {
        // Try to refresh token
        const refreshToken = localStorage.getItem(AUTH_CONFIG.refreshTokenKey);
        
        if (refreshToken) {
          // In a real app, call refresh token endpoint
          // const response = await axios.post('/auth/refresh', { refreshToken });
          // localStorage.setItem(AUTH_CONFIG.tokenKey, response.data.token);
          // return api(originalRequest);
          
          console.warn('Token refresh not implemented - redirecting to login');
        }
        
        // If refresh fails or no refresh token, redirect to login
        localStorage.removeItem(AUTH_CONFIG.tokenKey);
        localStorage.removeItem(AUTH_CONFIG.refreshTokenKey);
        window.location.href = '/login';
      } catch (refreshError) {
        console.error('Token refresh failed:', refreshError);
        window.location.href = '/login';
      }
    }
    
    // Format and return error
    const formattedError = formatError(error);
    
    console.error('❌ API Error:', {
      status: formattedError.status,
      message: formattedError.message,
      url: error.config?.url,
    });
    
    return Promise.reject(formattedError);
  }
);

/**
 * Error Types Enum
 */
export const ErrorType = {
  NETWORK: 'NETWORK_ERROR',
  VALIDATION: 'VALIDATION_ERROR',
  AUTHENTICATION: 'AUTHENTICATION_ERROR',
  AUTHORIZATION: 'AUTHORIZATION_ERROR',
  NOT_FOUND: 'NOT_FOUND_ERROR',
  SERVER: 'SERVER_ERROR',
  TIMEOUT: 'TIMEOUT_ERROR',
  CANCELLED: 'CANCELLED_ERROR',
  UNKNOWN: 'UNKNOWN_ERROR',
};

/**
 * Format error into a consistent structure
 * @param {Error} error - Axios error object
 * @returns {Object} Formatted error object
 */
const formatError = (error) => {
  // Network error (no response)
  if (!error.response) {
    if (error.code === 'ECONNABORTED' || error.code === 'ETIMEDOUT') {
      return {
        type: ErrorType.TIMEOUT,
        message: 'Request timeout. Please check your connection and try again.',
        status: 0,
        originalError: error,
      };
    }
    
    return {
      type: ErrorType.NETWORK,
      message: 'Network error. Please check your internet connection.',
      status: 0,
      originalError: error,
    };
  }

  const status = error.response.status;
  const data = error.response.data;
  
  // Map status codes to error types
  let type = ErrorType.UNKNOWN;
  let message = data?.message || error.message || 'An unexpected error occurred';

  if (status === 400) {
    type = ErrorType.VALIDATION;
    message = data?.message || 'Invalid request data';
  } else if (status === 401) {
    type = ErrorType.AUTHENTICATION;
    message = data?.message || 'Authentication required';
  } else if (status === 403) {
    type = ErrorType.AUTHORIZATION;
    message = data?.message || 'You do not have permission to perform this action';
  } else if (status === 404) {
    type = ErrorType.NOT_FOUND;
    message = data?.message || 'Resource not found';
  } else if (status >= 500) {
    type = ErrorType.SERVER;
    message = data?.message || 'Server error. Please try again later.';
  }

  return {
    type,
    message,
    status,
    data,
    errors: data?.errors || null, // Validation errors
    originalError: error,
  };
};

/**
 * Base API Service Class
 * Provides common methods for API services
 */
export class BaseService {
  constructor(baseEndpoint) {
    this.baseEndpoint = baseEndpoint;
  }

  /**
   * GET request
   * @param {string} endpoint - API endpoint (relative to baseEndpoint)
   * @param {Object} config - Axios config
   */
  async get(endpoint = '', config = {}) {
    const url = this.baseEndpoint + endpoint;
    return api.get(url, config);
  }

  /**
   * POST request
   * @param {string} endpoint - API endpoint
   * @param {Object} data - Request payload
   * @param {Object} config - Axios config
   */
  async post(endpoint = '', data = {}, config = {}) {
    const url = this.baseEndpoint + endpoint;
    return api.post(url, data, config);
  }

  /**
   * PUT request
   * @param {string} endpoint - API endpoint
   * @param {Object} data - Request payload
   * @param {Object} config - Axios config
   */
  async put(endpoint = '', data = {}, config = {}) {
    const url = this.baseEndpoint + endpoint;
    return api.put(url, data, config);
  }

  /**
   * PATCH request
   * @param {string} endpoint - API endpoint
   * @param {Object} data - Request payload
   * @param {Object} config - Axios config
   */
  async patch(endpoint = '', data = {}, config = {}) {
    const url = this.baseEndpoint + endpoint;
    return api.patch(url, data, config);
  }

  /**
   * DELETE request
   * @param {string} endpoint - API endpoint
   * @param {Object} config - Axios config
   */
  async delete(endpoint = '', config = {}) {
    const url = this.baseEndpoint + endpoint;
    return api.delete(url, config);
  }

  /**
   * GET request with cancellation support
   * @param {string} endpoint - API endpoint
   * @param {string} cancelKey - Unique key for cancellation
   * @param {Object} config - Axios config
   */
  async getWithCancel(endpoint = '', cancelKey, config = {}) {
    const url = this.baseEndpoint + endpoint;
    return api.get(url, { ...config, cancelKey });
  }

  /**
   * POST request with cancellation support
   * @param {string} endpoint - API endpoint
   * @param {Object} data - Request payload
   * @param {string} cancelKey - Unique key for cancellation
   * @param {Object} config - Axios config
   */
  async postWithCancel(endpoint = '', data = {}, cancelKey, config = {}) {
    const url = this.baseEndpoint + endpoint;
    return api.post(url, data, { ...config, cancelKey });
  }
}

// API helper methods (for backward compatibility)
export const apiHelpers = {
  /**
   * GET request
   */
  get: (url, config) => api.get(url, config),
  
  /**
   * POST request
   */
  post: (url, data, config) => api.post(url, data, config),
  
  /**
   * PUT request
   */
  put: (url, data, config) => api.put(url, data, config),
  
  /**
   * PATCH request
   */
  patch: (url, data, config) => api.patch(url, data, config),
  
  /**
   * DELETE request
   */
  delete: (url, config) => api.delete(url, config),
};

export default api;
